<script setup lang="ts">
import { Eye as EyeIcon, EyeOff as EyeOffIcon, UserCheck as UserCheckIcon } from '@lucide/vue'
import { useField, useForm } from 'vee-validate'
import { ref, onMounted, nextTick } from 'vue'

import BaseCheckbox from '@/components/ui/BaseCheckbox.vue'
import BaseSpinner from '@/components/ui/BaseSpinner.vue'
import { apiService } from '@/services/apiService'
import {
  CURRENT_TERMS_VERSION,
  PRIVACY_POLICY_PATH,
  TERMS_OF_USE_PATH,
} from '@/services/legalDocumentService'
import { useAuthStore } from '@/stores/authStore'
import {
  validateOptionalPassword,
  createOptionalConfirmPasswordValidator,
} from '@/utils/passwordValidation'
import { validateProfileName } from '@/utils/profileValidation'

interface Props {
  invitationToken?: string
  error?: string
  isLoading?: boolean
  invitationInfo?: { inviter: string; workspaceName: string; userExists: boolean }
}

interface Emits {
  success: [data: { password: string | undefined; profileName: string; workspaceClientId?: string }]
  error: [message: string]
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

// Auth store
const authStore = useAuthStore()

// Password visibility
const showPassword = ref(false)
const showConfirmPassword = ref(false)

// Refs for focus management
const profileNameInputRef = ref<HTMLInputElement | null>(null)
const submitButtonRef = ref<HTMLButtonElement | null>(null)

// Form setup
const { handleSubmit } = useForm()

// Profile name validation function
const validateName = validateProfileName

// Confirm password validation function
const validateConfirmPassword = createOptionalConfirmPasswordValidator(() => password.value)
const validateTermsAcceptance = (value: boolean) =>
  value === true || 'You must agree to the Terms of Use to create an account'

// Form fields
const {
  value: profileName,
  errorMessage: profileNameError,
  meta: profileNameMeta,
} = useField('profileName', validateName)
const {
  value: password,
  errorMessage: passwordError,
  meta: passwordMeta,
} = useField('password', validateOptionalPassword)
const {
  value: confirmPassword,
  errorMessage: confirmPasswordError,
  meta: confirmPasswordMeta,
} = useField('confirmPassword', validateConfirmPassword)
const {
  value: acceptedTerms,
  errorMessage: acceptedTermsError,
  handleBlur: handleAcceptedTermsBlur,
} = useField<boolean>('acceptedTerms', validateTermsAcceptance, {
  initialValue: false,
})

const togglePasswordVisibility = () => {
  showPassword.value = !showPassword.value
}

const toggleConfirmPasswordVisibility = () => {
  showConfirmPassword.value = !showConfirmPassword.value
}

// Form submission for existing users (simplified)
const onSubmitExistingUser = async () => {
  if (!props.invitationToken) {
    emit('error', 'No invitation token provided.')
    return
  }

  try {
    const invitationResult = await apiService.acceptInvitation(props.invitationToken)

    if (!invitationResult.success) {
      emit('error', invitationResult.message || 'Failed to accept invitation.')
      return
    }

    // Set user info from invitation acceptance
    if (invitationResult.userId && invitationResult.userEmail) {
      await authStore.setUser({
        userId: invitationResult.userId,
        email: invitationResult.userEmail,
        name: invitationResult.userProfileName || null,
        sessionToken: authStore.user?.sessionToken ?? null,
      })
    }

    // Emit success with workspace ID for navigation
    emit('success', {
      password: undefined,
      profileName: invitationResult.userProfileName || 'User',
      workspaceClientId: invitationResult.workspaceClientId,
    })
  } catch (error) {
    emit('error', error instanceof Error ? error.message : String(error))
  }
}

// Form submission for new users
const onSubmit = handleSubmit(async (values) => {
  if (!props.invitationToken) {
    emit('error', 'No invitation token provided.')
    return
  }

  try {
    // Accept the invitation with profile data in a single call
    const profileData = {
      profileName: values.profileName as string,
      password: values.password ? (values.password as string) : undefined,
      acceptedTerms: values.acceptedTerms as boolean,
      termsVersion: CURRENT_TERMS_VERSION,
    }

    const invitationResult = await apiService.acceptInvitation(props.invitationToken, profileData)

    if (!invitationResult.success) {
      emit('error', invitationResult.message || 'Failed to accept invitation.')
      return
    }

    // Set user info from invitation acceptance
    if (invitationResult.userId && invitationResult.userEmail) {
      await authStore.setUser({
        userId: invitationResult.userId,
        email: invitationResult.userEmail,
        name: invitationResult.userProfileName || profileData.profileName,
        sessionToken: authStore.user?.sessionToken ?? null,
      })
    }

    // Emit success with workspace ID for navigation
    emit('success', {
      password: profileData.password,
      profileName: profileData.profileName,
      workspaceClientId: invitationResult.workspaceClientId,
    })
  } catch (error) {
    emit('error', error instanceof Error ? error.message : String(error))
  }
})

// Focus management
onMounted(() => {
  nextTick(() => {
    // Only focus on profile name input for new users
    if (!props.invitationInfo?.userExists && profileNameInputRef.value) {
      profileNameInputRef.value.focus()
    }
  })
})
</script>

<template>
  <!-- Add live region for form status announcements -->
  <div id="invitation-status" class="sr-only" aria-live="polite" aria-atomic="true">
    <span v-if="isLoading">Setting up profile, please wait...</span>
    <span v-else-if="error">{{ error }}</span>
  </div>

  <!-- Welcome Header -->
  <div class="mb-6 text-center">
    <div
      class="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary-100"
    >
      <UserCheckIcon class="h-6 w-6 text-primary-600" />
    </div>

    <template v-if="invitationInfo">
      <p class="mt-2 text-sm text-gray-600">
        <strong>{{ invitationInfo.inviter }}</strong> invited you to join
        <strong>{{ invitationInfo.workspaceName }}</strong>
      </p>
    </template>
    <template v-else>
      <p class="mt-2 text-sm text-gray-600">Thanks for accepting the invitation!</p>
    </template>

    <template v-if="invitationInfo?.userExists">
      <p class="mt-2 text-sm text-gray-500">Click the button below to join the workspace.</p>
    </template>
    <template v-else>
      <p class="mt-2 text-sm text-gray-500">Please set up your profile to get started.</p>
    </template>
  </div>

  <!-- Existing User View -->
  <template v-if="invitationInfo?.userExists">
    <!-- Error message for existing users -->
    <div
      v-if="error"
      class="p-3 bg-red-50 border border-red-200 rounded-md mb-4"
      role="alert"
      aria-live="polite"
    >
      <p class="text-sm text-red-600">{{ error }}</p>
    </div>

    <!-- Simple button for existing users -->
    <button
      type="button"
      @click="onSubmitExistingUser"
      :disabled="isLoading"
      :aria-describedby="isLoading ? 'existing-user-loading-status' : undefined"
      :class="[
        // Layout & sizing
        'w-full',
        'flex',
        'items-center',
        'justify-center',
        'px-4',
        'py-2',
        // Colors & states
        'bg-primary-600',
        'hover:bg-primary-700',
        'disabled:bg-primary-400',
        'text-white',
        // Typography & effects
        'font-medium',
        'rounded-md',
        'transition-colors',
        // Focus & interaction
        'focus:outline-none',
        'focus:ring-2',
        'focus:ring-primary-500',
        'focus:ring-offset-2',
        'hover:cursor-pointer',
      ]"
    >
      <UserCheckIcon v-if="!isLoading" class="w-4 h-4 mr-2" aria-hidden="true" />
      <BaseSpinner v-else class="mr-2" aria-hidden="true" />
      {{ isLoading ? 'Joining...' : 'Join Workspace' }}
    </button>
    <div v-if="isLoading" id="existing-user-loading-status" class="sr-only">
      Please wait while we add you to the workspace
    </div>
  </template>

  <!-- New User Form -->
  <form v-else @submit.prevent="onSubmit" class="space-y-4" novalidate>
    <!-- Profile Name -->
    <div>
      <label for="invitation-profile-name" class="block text-sm font-medium text-gray-700 mb-2">
        Profile Name (visible to others) <span class="text-red-500" aria-label="required">*</span>
      </label>
      <input
        id="invitation-profile-name"
        ref="profileNameInputRef"
        v-model="profileName"
        type="text"
        autocomplete="name"
        required
        class="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 transition-colors"
        :class="{
          'border-red-300 focus:ring-red-500': profileNameError,
          'border-green-300 focus:ring-green-500': profileNameMeta.valid && profileNameMeta.touched,
          'border-gray-300 focus:ring-primary-500':
            !profileNameError && (!profileNameMeta.valid || !profileNameMeta.touched),
        }"
        placeholder="Enter your name"
        :disabled="isLoading"
        :aria-invalid="profileNameError ? 'true' : 'false'"
        :aria-describedby="
          profileNameError ? 'invitation-profile-name-error' : 'invitation-profile-name-help'
        "
      />
      <div v-if="!profileNameError" id="invitation-profile-name-help" class="sr-only">
        Enter your display name for your profile
      </div>
      <div
        v-if="profileNameError"
        id="invitation-profile-name-error"
        class="mt-1 text-sm text-red-600"
        role="alert"
      >
        {{ profileNameError }}
      </div>
    </div>

    <!-- Password Hint -->
    <div class="rounded-md bg-primary-50 p-3">
      <p class="text-xs text-primary-700">
        <strong>Note:</strong> Setting a password is optional for now. You can always change it
        later using the password reset function.
      </p>
    </div>

    <!-- Password -->
    <div>
      <label for="invitation-password" class="block text-sm font-medium text-gray-700 mb-2">
        Password (optional)
      </label>
      <div class="relative">
        <input
          id="invitation-password"
          v-model="password"
          :type="showPassword ? 'text' : 'password'"
          autocomplete="new-password"
          class="w-full px-3 py-2 pr-10 border rounded-md focus:outline-none focus:ring-2 transition-colors"
          :class="{
            'border-red-300 focus:ring-red-500': passwordError,
            'border-green-300 focus:ring-green-500': passwordMeta.valid && passwordMeta.touched,
            'border-gray-300 focus:ring-primary-500':
              !passwordError && (!passwordMeta.valid || !passwordMeta.touched),
          }"
          placeholder="Create a secure password (optional)"
          :disabled="isLoading"
          :aria-invalid="passwordError ? 'true' : 'false'"
          :aria-describedby="
            passwordError ? 'invitation-password-error' : 'invitation-password-help'
          "
        />
        <button
          type="button"
          @click="togglePasswordVisibility"
          :class="[
            // Position & layout
            'absolute',
            'right-3',
            'top-1/2',
            'transform',
            '-translate-y-1/2',
            'p-1',
            // Colors & interaction
            'text-gray-400',
            'hover:text-gray-600',
            'hover:cursor-pointer',
            // Focus state
            'focus:outline-none',
            'focus:ring-2',
            'focus:ring-primary-500',
            'focus:ring-offset-2',
            // Shape
            'rounded-md',
          ]"
          :aria-label="showPassword ? 'Hide password' : 'Show password'"
          :aria-pressed="showPassword"
          :disabled="isLoading"
          tabindex="0"
        >
          <EyeOffIcon v-if="showPassword" class="w-5 h-5" />
          <EyeIcon v-else class="w-5 h-5" />
        </button>
      </div>
      <div v-if="!passwordError" id="invitation-password-help" class="sr-only">
        Optionally create a password for your account
      </div>
      <div
        v-if="passwordError"
        id="invitation-password-error"
        class="mt-1 text-sm text-red-600"
        role="alert"
      >
        {{ passwordError }}
      </div>
    </div>

    <!-- Confirm Password -->
    <div>
      <label for="invitation-confirm-password" class="block text-sm font-medium text-gray-700 mb-2">
        Confirm Password (optional)
      </label>
      <div class="relative">
        <input
          id="invitation-confirm-password"
          v-model="confirmPassword"
          :type="showConfirmPassword ? 'text' : 'password'"
          autocomplete="new-password"
          class="w-full px-3 py-2 pr-10 border rounded-md focus:outline-none focus:ring-2 transition-colors"
          :class="{
            'border-red-300 focus:ring-red-500': confirmPasswordError,
            'border-green-300 focus:ring-green-500':
              confirmPasswordMeta.valid && confirmPasswordMeta.touched,
            'border-gray-300 focus:ring-primary-500':
              !confirmPasswordError && (!confirmPasswordMeta.valid || !confirmPasswordMeta.touched),
          }"
          placeholder="Confirm your password (optional)"
          :disabled="isLoading"
          :aria-invalid="confirmPasswordError ? 'true' : 'false'"
          :aria-describedby="
            confirmPasswordError
              ? 'invitation-confirm-password-error'
              : 'invitation-confirm-password-help'
          "
        />
        <button
          type="button"
          @click="toggleConfirmPasswordVisibility"
          :class="[
            // Position & layout
            'absolute',
            'right-3',
            'top-1/2',
            'transform',
            '-translate-y-1/2',
            'p-1',
            // Colors & interaction
            'text-gray-400',
            'hover:text-gray-600',
            'hover:cursor-pointer',
            // Focus state
            'focus:outline-none',
            'focus:ring-2',
            'focus:ring-primary-500',
            'focus:ring-offset-2',
            // Shape
            'rounded-md',
          ]"
          :aria-label="
            showConfirmPassword ? 'Hide password confirmation' : 'Show password confirmation'
          "
          :aria-pressed="showConfirmPassword"
          :disabled="isLoading"
          tabindex="0"
        >
          <EyeOffIcon v-if="showConfirmPassword" class="w-5 h-5" />
          <EyeIcon v-else class="w-5 h-5" />
        </button>
      </div>
      <div v-if="!confirmPasswordError" id="invitation-confirm-password-help" class="sr-only">
        Confirm your password if you entered one
      </div>
      <div
        v-if="confirmPasswordError"
        id="invitation-confirm-password-error"
        class="mt-1 text-sm text-red-600"
        role="alert"
      >
        {{ confirmPasswordError }}
      </div>
    </div>

    <div>
      <label class="flex items-start gap-3 text-sm text-gray-700">
        <BaseCheckbox
          v-model="acceptedTerms"
          class="mt-0.5 shrink-0"
          :disabled="isLoading"
          :aria-invalid="acceptedTermsError ? 'true' : 'false'"
          :aria-describedby="
            acceptedTermsError ? 'invitation-terms-error' : 'invitation-terms-help'
          "
          @blur="handleAcceptedTermsBlur"
        />
        <span>
          I confirm that I am at least 16 and have parent or guardian permission where required. I
          agree to the
          <a
            :href="TERMS_OF_USE_PATH"
            target="_blank"
            rel="noopener noreferrer"
            class="font-medium text-primary-600 hover:text-primary-800 hover:underline"
          >
            Terms of Use
          </a>
          and acknowledge the
          <a
            :href="PRIVACY_POLICY_PATH"
            target="_blank"
            rel="noopener noreferrer"
            class="font-medium text-primary-600 hover:text-primary-800 hover:underline"
          >
            Privacy Policy</a
          >.
        </span>
      </label>
      <p v-if="!acceptedTermsError" id="invitation-terms-help" class="sr-only">
        Agreement to the Terms of Use is required to create an account
      </p>
      <p v-else id="invitation-terms-error" class="mt-1 text-sm text-red-600" role="alert">
        {{ acceptedTermsError }}
      </p>
    </div>

    <!-- Error message -->
    <div
      v-if="error"
      class="p-3 bg-red-50 border border-red-200 rounded-md"
      role="alert"
      aria-live="polite"
    >
      <p class="text-sm text-red-600">{{ error }}</p>
    </div>

    <!-- Submit button -->
    <button
      ref="submitButtonRef"
      type="submit"
      :disabled="isLoading"
      :aria-describedby="isLoading ? 'invitation-loading-status' : undefined"
      :class="[
        // Layout & sizing
        'w-full',
        'flex',
        'items-center',
        'justify-center',
        'px-4',
        'py-2',
        // Colors & states
        'bg-primary-600',
        'hover:bg-primary-700',
        'disabled:bg-primary-400',
        'text-white',
        // Typography & effects
        'font-medium',
        'rounded-md',
        'transition-colors',
        // Focus & interaction
        'focus:outline-none',
        'focus:ring-2',
        'focus:ring-primary-500',
        'focus:ring-offset-2',
        'hover:cursor-pointer',
      ]"
    >
      <UserCheckIcon v-if="!isLoading" class="w-4 h-4 mr-2" aria-hidden="true" />
      <BaseSpinner v-else class="mr-2" aria-hidden="true" />
      {{ isLoading ? 'Setting up...' : 'Complete Setup' }}
    </button>
    <div v-if="isLoading" id="invitation-loading-status" class="sr-only">
      Please wait while we set up your profile
    </div>
  </form>
</template>
